import { API_BASE_URL } from '../config.js';

// Single-flight refresh token queue state (Shared across all BaseApiService instances)
let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshed(newToken) {
  refreshSubscribers.forEach(cb => cb(null, newToken));
  refreshSubscribers = [];
}

function onRefreshFailed(error) {
  refreshSubscribers.forEach(cb => cb(error, null));
  refreshSubscribers = [];
}

class BaseApiService {
  constructor() {
    this.baseURL = API_BASE_URL || 'http://localhost:9980';
    this.publicEndpoints = new Set([
      '/api/admin/college/login',
      '/api/teacher/login',
      '/api/parent/login',
      '/api/driver/login',
      '/api/admin/college/forgot-password',
      '/api/admin/college/reset-password',
      '/api/superadmin/login',
      '/api/health',
      '/api/auth/refresh-token',
      '/api/auth/logout'
    ]);
  }

  // Retrieve current active JWT access token
  getToken() {
    return localStorage.getItem('authToken') || localStorage.getItem('superAdminToken');
  }

  // Retrieve refresh token
  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  // Save tokens
  setTokens(accessToken, refreshToken) {
    if (accessToken) localStorage.setItem('authToken', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  }

  // Save single token alias
  setToken(token) {
    if (token) localStorage.setItem('authToken', token);
  }

  // Remove token alias
  removeToken() {
    this.clearAuthSession();
  }

  // Remove tokens and session state
  clearAuthSession() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('superAdminToken');
    localStorage.removeItem('orgData');
    localStorage.removeItem('user');
    sessionStorage.clear();
  }

  // Common headers for authenticated requests
  getHeaders(customToken = null) {
    const headers = {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    };

    const token = customToken || this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const u = JSON.parse(userStr);
        if (u.orgId) headers['X-Org-Id'] = String(u.orgId);
      } catch (e) {}
    }

    const storedOrgId = localStorage.getItem('orgId') || sessionStorage.getItem('orgId');
    if (storedOrgId && !headers['X-Org-Id']) {
      headers['X-Org-Id'] = String(storedOrgId);
    }

    return headers;
  }

  /**
   * Execute single-flight refresh token rotation call
   */
  async executeTokenRefresh() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${this.baseURL}/api/auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify({ refreshToken })
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Token refresh rejected: ${errText || response.statusText}`);
    }

    const data = await response.json();
    if (!data || !data.success || !data.accessToken) {
      throw new Error(data?.error || 'Invalid refresh token response');
    }

    this.setTokens(data.accessToken, data.refreshToken);
    return data.accessToken;
  }

  /**
   * Centralized HTTP Request Interceptor with automatic JWT rotation and retry
   */
  async makeRequest(endpoint, options = {}, isRetry = false) {
    const url = `${this.baseURL}${endpoint}`;
    const normalizedEndpoint = endpoint?.split('?')[0] || '';
    const token = this.getToken();
    const requiresAuth = !this.publicEndpoints.has(normalizedEndpoint) &&
      !normalizedEndpoint.startsWith('/api/student/organization/') &&
      !normalizedEndpoint.startsWith('/api/student/pre-register/') &&
      !normalizedEndpoint.startsWith('/api/departments/org/string/');

    if (requiresAuth && !token) {
      throw new Error('Authentication required. Please login to continue.');
    }

    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...(options.headers || {})
      },
    };

    try {
      const response = await fetch(url, config);

      // Handle 401 Unauthorized via Single-Flight Token Refresh
      if (response.status === 401 && requiresAuth && !isRetry) {
        const currentRefreshToken = this.getRefreshToken();

        if (!currentRefreshToken) {
          this.clearAuthSession();
          window.dispatchEvent(new Event('sessionExpired'));
          throw new Error('Session expired. Please log in again.');
        }

        if (isRefreshing) {
          // Another request is already refreshing; queue this request until refresh completes
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh((err, newToken) => {
              if (err) {
                reject(err);
              } else {
                const retryOptions = {
                  ...options,
                  headers: {
                    ...(options.headers || {}),
                    'Authorization': `Bearer ${newToken}`
                  }
                };
                resolve(this.makeRequest(endpoint, retryOptions, true));
              }
            });
          });
        }

        // First request to encounter 401 initiates single-flight refresh
        isRefreshing = true;
        try {
          const newAccessToken = await this.executeTokenRefresh();
          isRefreshing = false;
          onRefreshed(newAccessToken);

          // Retry the current request with the newly issued access token
          const retryOptions = {
            ...options,
            headers: {
              ...(options.headers || {}),
              'Authorization': `Bearer ${newAccessToken}`
            }
          };
          return await this.makeRequest(endpoint, retryOptions, true);
        } catch (refreshErr) {
          isRefreshing = false;
          onRefreshFailed(refreshErr);
          this.clearAuthSession();
          window.dispatchEvent(new Event('sessionExpired'));
          throw new Error('Your session has expired. Please sign in again.');
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        return await response.text();
      }
    } catch (error) {
      throw error;
    }
  }

  // Connectivity Health Check
  async testConnection() {
    try {
      const response = await fetch(`${this.baseURL}/api/health`, {
        headers: {
          'ngrok-skip-browser-warning': 'true',
          'Accept': 'application/json',
        },
      });
      return response.ok || response.status < 500;
    } catch (error) {
      return false;
    }
  }
}

export default BaseApiService;
